import React, { useState, useEffect, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_IMAGE_CLEAR_BUTTON_SX as IMAGE_CLEAR_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    APP_COMPANY_CONTACT_IMAGE_FILE_ACCEPT,
    APP_COMPANY_CONTACT_IMAGE_FILE_ERROR_MESSAGE,
    APP_COMPANY_CONTACT_IMAGE_LEAVE_MESSAGE,
    hasPendingCompanyContactImage,
    isCompanyContactImageFile,
    usePendingCompanyContactImageUnloadGuard,
} from './appCompanyContactImageLeaveGuard'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    image: '',
})

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PANEL_HEADER_SX = {
    mb: 1.5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1.5,
}

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function ImageUploadSection({
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
    hasPendingFile = false,
    onClear,
}) {
    const imageFileName = getImageFileName(imageFile)
    const fileInputRef = useRef(null)

    const handleClearClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        onClear?.()
    }

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={IMAGE_PANEL_HEADER_SX}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        도입문의 이미지
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        도입문의 랜딩 영역에 노출되는 이미지를 확인하고 교체합니다.
                    </Typography>
                </Box>
                {hasPendingFile && (
                    <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveIcon />}
                        onClick={handleClearClick}
                        sx={IMAGE_CLEAR_BUTTON_SX}
                        disabled={disabled}
                    >
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="도입문의 이미지 미리보기"
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: APP_COMPANY_CONTACT_IMAGE_FILE_ACCEPT }}
                    inputRef={fileInputRef}
                    style={{ display: 'none' }}
                    name="image"
                    id="image"
                    type="file"
                    disabled={disabled}
                    onChange={onUploadClick}
                />
                <Box component="label" htmlFor="image" sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        이미지 선택
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function AppCompanyContactImage(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.companyContactImageId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: '' }
    const listSearch = location.search || ''
    const [detailLoading, setDetailLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [mainImage, setMainImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const originalImageRef = useRef({ image: '', preview: '' })
    const hasPendingImage = hasPendingCompanyContactImage(params.image)
    const leaveGuardEnabled = hasPendingImage && !saving && !navigationUnlocked

    usePendingCompanyContactImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 도입문의 이미지 상세를 불러올 수 없습니다.'
                setParams(createDefaultParams())
                setMainImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setDetailLoading(false)
                return
            }

            setDetailLoading(true)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/appCompany/getContactImageDetail', { companyContactImageId: id })

                if (data.code === 200) {
                    const baseParams = data.data || {}

                    if (!baseParams.companyContactImageId) {
                        setParams(createDefaultParams())
                        setMainImage('')
                        setDetailErrorMessage('도입문의 이미지 상세 정보를 찾을 수 없습니다.')
                        return
                    }

                    const previewImage = baseParams.image === null ? '/images/noimage.jpg' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}`

                    originalImageRef.current = {
                        image: baseParams.image,
                        preview: previewImage,
                    }
                    setParams(baseParams)
                    setMainImage(previewImage)
                    setDetailErrorMessage('')
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '도입문의 이미지 상세를 불러오지 못했습니다.'
                    setParams(createDefaultParams())
                    setMainImage('')
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '도입문의 이미지 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setMainImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setDetailLoading(false)
            }
        }
        getData()
    }, [history, id])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 query는 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/appCompanyContactImages',
                search: listSearch,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
                searchText: searchText,
                searchSelect: searchSelect,
                curPage: curPage
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (leaveGuardEnabled) {
            event?.preventDefault()

            if (!window.confirm(APP_COMPANY_CONTACT_IMAGE_LEAVE_MESSAGE)) {
                return
            }
        }

        moveToList()
    }

    const handleUploadClick = (e) => {
        e.preventDefault()
        const { name, files } = e.target
        const file = files[0]

        if (!file) {
            return
        }

        if (!isCompanyContactImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(APP_COMPANY_CONTACT_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        const reader = new FileReader()

        reader.onloadend = () => {
            setParams(prevParams => ({
                ...prevParams,
                [name]: file
            }))

            setMainImage(reader.result)
        }
    
        reader.readAsDataURL(file)
    }

    const handleClearSelectedImage = () => {
        setParams(prevParams => ({
            ...prevParams,
            image: originalImageRef.current.image,
        }))
        setMainImage(originalImageRef.current.preview)
    }

    const updateData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appCompany/updateContactImage',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 저장 후에도 운영자가 보던 도입문의 이미지 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도입문의 이미지를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        if (!params.image) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('이미지를 업로드해주세요.')
            return
        }

        const formData = new FormData()
        formData.append('companyContactImageId',id)
        formData.append('image',params.image)

        updateData(formData)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="도입문의 이미지 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    if (detailLoading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!params?.companyContactImageId) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>도입문의 이미지 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Prompt when={leaveGuardEnabled} message={APP_COMPANY_CONTACT_IMAGE_LEAVE_MESSAGE} />
                <Title>도입문의 이미지 수정</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12}>
                            <ImageUploadSection
                                imageSrc={mainImage}
                                imageFile={params.image}
                                disabled={saving}
                                onUploadClick={handleUploadClick}
                                hasPendingFile={params.image instanceof File}
                                onClear={handleClearSelectedImage}
                            />
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appCompanyContactImages" search={listSearch} visible={true} searchText={searchText} searchSelect={searchSelect} curPage={curPage} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={handleBackClick} />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
